import { useCallback, useContext, useEffect, useState } from "react";
import { Vehicle } from "../interfaces";
import useLiveApi from "./use-live-api";
import { AuthContext, IAuthContext } from "react-oauth2-code-pkce";
import { usePagination } from "./usePagination";

interface VehiclesApi {
  data: Vehicle[];
  loading: boolean;
  error: boolean;
  nextPage: () => Promise<void>;
  prevPage: () => Promise<void>;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const fetchItems = async (
  apiGatewayFetch: (
    url: string,
    method: string,
    body?: string
  ) => Promise<Response>,
  registration?: string,
  pageSize?: number,
  paginationToken?: string
) => {
  let url = `/vehicles`;
  const query = new URLSearchParams();
  if (pageSize) query.set("limit", String(pageSize));
  if (registration) query.set("registration", registration);
  if (Array.from(query.keys()).length > 0) url += `?${query.toString()}`;
  if (paginationToken) url += `?token=${paginationToken}`;
  const response = await apiGatewayFetch(url, "get");
  if (response.status !== 200) {
    throw new Error(response.status.toString());
  }
  return await response.json();
};

export const useVehiclesForCurrentAccount = (
  registration?: string,
  pageSize?: number,
  poll?: boolean
): VehiclesApi => {
  const { apiGatewayFetch } = useLiveApi();
  const auth: IAuthContext = useContext(AuthContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [data, setData] = useState<Vehicle[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [pageTokens, setPageTokens] = useState<(string | null)[]>([""]); // page 1 has no token
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

  const loadPage = useCallback(
    async (paginationToken?: string) => {
      setLoading(true);
      setError(false);
      try {
        const data = await fetchItems(
          apiGatewayFetch,
          registration,
          pageSize,
          paginationToken
        );
        setData(data.items as Vehicle[]);
        setNextToken(data.paginationToken ?? null);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [apiGatewayFetch, registration, pageSize]
  );

  const { nextPage, prevPage, hasNextPage, hasPrevPage } = usePagination({
    loadPage,
    nextToken,
    currentPageIndex,
    pageTokens,
    setPageTokens,
    setCurrentPageIndex,
  });

  useEffect(() => {
    setPageTokens([""]);
    setCurrentPageIndex(0);
    loadPage(); // Initial load without token

    if (poll || poll === undefined) {
      const interval = setInterval(() => loadPage(), 60000);
      return () => clearInterval(interval);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this once
  }, [registration, auth.token]);

  return { data, nextPage, prevPage, hasNextPage, hasPrevPage, loading, error };
};
